import { classifyChange } from '@pfo/analytics';
import { currentPeriod, periodRange, type Period } from '@pfo/shared';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useSWRConfig } from 'swr';

import { useColorScheme } from '../../../shared/hooks/useColorScheme.js';
import { cx } from '../../../shared/lib/cx.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { ACCOUNTS_KEY } from '../../accounts/hooks/useAccounts.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { PendingCard } from '../../pending/components/PendingCard.js';
import { DueCard } from '../../recurring/components/DueCard.js';
import { todayInTimeZone } from '../../recurring/data/recurringRepository.js';
import { useObligations } from '../../recurring/hooks/useObligations.js';
import { useTimeline } from '../../transactions/hooks/useTimeline.js';
import { timeLabelOf } from '../../transactions/model/timelineGroups.js';
import { ENTRY_TYPE_LABELS } from '../../transactions/model/transactionLabels.js';
import { AllocationCard } from '../components/AllocationCard.js';
import { BreakdownCard } from '../components/BreakdownCard.js';
import { MonthSwitcher } from '../components/MonthSwitcher.js';
import { SummaryTabs, type SummaryTab } from '../components/SummaryTabs.js';
import { useMoneyFlow } from '../hooks/useMoneyFlow.js';
import { changeSentence } from '../model/homeLabels.js';
import styles from './HomePage.module.css';

/** How many of the newest rows the dashboard shows before handing over to the timeline. */
const RECENT_LIMIT = 5;

/**
 * Home — the month at a glance (plan step 1.4, spec v2 22.2).
 *
 * Reading order follows the question a person opens the app with: which month am I looking at,
 * how much went out and came in, is that better or worse than last month, what did it go on,
 * and only then the ledger's own view — where the money was allocated, and what happened most
 * recently.
 *
 * Chi tiêu and Thu nhập are tabs (PO layout 2026-08-19): the figure you tap decides which
 * breakdown the card below draws.
 *
 * Every figure arrives from `app.money_flow` already summed. Nothing here adds two amounts of
 * money together (DBD-014).
 */
export function HomePage() {
  const contextState = useCurrentContext();
  const theme = useColorScheme();
  const recentHeadingId = useId();
  const breakdownId = useId();
  const [notice, setNotice] = useState<string | null>(null);
  const { mutate } = useSWRConfig();

  const timeZone =
    contextState.status === 'ready' ? contextState.context.household.timezone : 'UTC';
  const current = currentPeriod(timeZone);
  const [period, setPeriod] = useState<Period | null>(null);
  const [tab, setTab] = useState<SummaryTab>('expense');
  const shown = period ?? current;

  const subjectId = contextState.status === 'ready' ? contextState.context.selfSubject.id : '';
  const flowState = useMoneyFlow(
    subjectId === '' ? null : { subjectId, year: shown.year, month: shown.month },
  );

  // The recent list follows the month being viewed (PO decision N8): every row on this
  // screen answers for the same month, cut in the household timezone like everything else.
  const shownRange = periodRange(shown.year, shown.month, timeZone);
  const recent = useTimeline({
    subjectScope: 'self',
    selfSubjectId: subjectId,
    from: shownRange.fromUtc,
    to: shownRange.toUtc,
    limit: RECENT_LIMIT,
  });

  // What is owed is measured from the real today, not from the month being browsed: stepping
  // back to July must not change what the household still has to pay this week.
  const obligations = useObligations(
    contextState.status === 'ready' ? todayInTimeZone(timeZone) : null,
  );

  /** A confirm posts a real entry, so everything that reads the ledger is reread. */
  async function refreshAfterConfirm() {
    await Promise.all([
      obligations.status === 'ready' ? obligations.refresh() : Promise.resolve(),
      recent.status === 'ready' ? recent.refresh() : Promise.resolve(),
      mutate(ACCOUNTS_KEY),
      mutate((key) => typeof key === 'string' && key.startsWith('money-flow:')),
    ]);
  }

  if (contextState.status === 'loading') {
    return <LoadingState label="Đang chuẩn bị không gian tài chính của bạn…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }

  const { context } = contextState;

  return (
    <div className={styles.page}>
      {/* The shell header carries the person's name; this names the screen, so the page has a
          heading of its own for a screen reader to land on. */}
      <h1 className={styles.title}>Tổng quan</h1>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}

      {/* Above the month's figures on purpose: these have NOT happened yet (B1). Always the
          real today, never the month being browsed — what is owed does not move when the
          user looks back at July. */}
      {obligations.status === 'ready' ? (
        <DueCard
          obligations={obligations.obligations}
          timeZone={timeZone}
          onConfirmed={(message) => {
            setNotice(message);
            void refreshAfterConfirm();
          }}
        />
      ) : null}

      {/* Bank notices from email (AL1): not on the books either, so they sit with Đến hạn. */}
      <PendingCard />

      <MonthSwitcher period={shown} current={current} onChange={setPeriod} />

      {flowState.status === 'loading' ? <LoadingState label="Đang tính số liệu…" /> : null}
      {flowState.status === 'error' ? (
        <ErrorState message={flowState.message} onRetry={flowState.retry} />
      ) : null}

      {flowState.status === 'ready' ? (
        <>
          <SummaryTabs
            active={tab}
            expenseTotal={flowState.flow.expense.total}
            incomeTotal={flowState.flow.moneyIn.total}
            panelId={breakdownId}
            onChange={setTab}
          />

          <ComparisonBanner
            tab={tab}
            delta={
              tab === 'expense'
                ? flowState.flow.comparison.expenseDelta
                : flowState.flow.comparison.incomeDelta
            }
            baseline={
              tab === 'expense'
                ? flowState.flow.comparison.expenseTotal
                : flowState.flow.comparison.incomeTotal
            }
          />

          <BreakdownCard
            mode={tab}
            id={breakdownId}
            expense={flowState.flow.expense}
            moneyIn={flowState.flow.moneyIn}
            period={shown}
            theme={theme}
          />

          {/* Where the money went is a statement about the whole month, not about one tab, so
              it stays put when the tabs switch. Hidden for a month with nothing in it: a card
              of zeroes says less than the empty state above it already did. */}
          {flowState.flow.expense.total !== 0 || flowState.flow.moneyIn.total !== 0 ? (
            <AllocationCard
              moneyIn={flowState.flow.moneyIn}
              allocation={flowState.flow.allocation}
            />
          ) : null}
        </>
      ) : null}

      {/* The newest rows OF THE MONTH BEING VIEWED (N8): on the current month this still
          answers "did what I just typed land?"; on a past month it shows that month's tail
          instead of today's, so every card on the screen reports the same month. */}
      {recent.status === 'ready' && recent.items.length > 0 ? (
        <section className={lists.card} aria-labelledby={recentHeadingId}>
          <div className={styles.cardHeading}>
            <h2 className={lists.cardTitle} id={recentHeadingId}>
              Giao dịch gần đây
            </h2>
            <Link className={styles.cardLink} to="/giao-dich">
              Xem tất cả
            </Link>
          </div>

          <ul className={lists.list}>
            {recent.items.slice(0, RECENT_LIMIT).map((item) => (
              <li key={item.id}>
                <div className={lists.row}>
                  <span className={lists.rowText}>
                    <span className={lists.rowName}>
                      {item.categoryName ??
                        item.incomeSourceName ??
                        item.description ??
                        ENTRY_TYPE_LABELS[item.entryType] ??
                        'Giao dịch'}
                    </span>
                    <span className={lists.rowMeta}>
                      {timeLabelOf(item.occurredAt, context.household.timezone)}
                      {item.fromAccountName ? ` · từ ${item.fromAccountName}` : ''}
                      {item.toAccountName ? ` · vào ${item.toAccountName}` : ''}
                    </span>
                  </span>
                  <span
                    className={cx(lists.amount, item.entryType === 'income' && styles.amountIncome)}
                  >
                    {item.entryType === 'income' ? '+' : ''}
                    {formatVnd(item.amount)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * "Giảm mạnh 3.737.930đ so với cùng kỳ tháng trước".
 *
 * Deterministic, not AI (spec 22.2): the delta comes from SQL, `classifyChange` turns the ratio
 * into a word. Silent when there is nothing to compare — a first month has no story. Which
 * figure it describes follows the selected tab, so the sentence always matches the number
 * above it.
 */
function ComparisonBanner({
  tab,
  delta,
  baseline,
}: {
  tab: SummaryTab;
  delta: number;
  baseline: number;
}) {
  if (delta === 0 && baseline === 0) return null;

  const verdict = classifyChange({ delta, baseline });
  // Spending less is good news; earning less is not. The same arrow, the opposite meaning.
  const isGood = tab === 'expense' ? verdict.direction === 'down' : verdict.direction === 'up';
  const tone = verdict.direction === 'flat' ? undefined : isGood ? styles.bannerGood : undefined;

  return (
    <p className={cx(styles.banner, tone)} role="status">
      <span className={styles.bannerIcon} aria-hidden="true">
        {verdict.direction === 'down' ? '↓' : verdict.direction === 'up' ? '↑' : '＝'}
      </span>
      {changeSentence(verdict, delta, tab === 'expense' ? 'Chi' : 'Thu')}
    </p>
  );
}
